import React, { ReactElement, useMemo } from 'react';
import { Field, getIn } from 'formik';
import { useItemCreationContext } from '~/ModularComponents/ItemCreation/ItemCreation';
import { TextField } from '~/UIKit';
import { CHARCOAL_GREY } from '~/Constants/Colors.constants';
import { makeStyles } from '~/UIKit/Style';

interface IInputTextProps<T> {
  name: keyof T;
  icon?: ReactElement;
  label: string;
  placeholder?: string;
  maxLength?: number;
  readOnly?: boolean;
}

const useStyles = makeStyles(
  {
    container: {
      color: CHARCOAL_GREY,
      width: '100%',
    },
  },
  { name: 'inputText' },
);

export const InputText = <T extends object>({
  name,
  icon,
  label,
  placeholder,
  maxLength,
  readOnly,
}: IInputTextProps<T>) => {
  const { isLoading, disabled, values, touched, errors } = useItemCreationContext();
  const { classes } = useStyles();

  const value = getIn(values, name as string);
  const isDisabled = getIn(disabled, name as string);
  const isTouched = getIn(touched, name as string);
  const error = getIn(errors, name as string);

  return useMemo(
    () => (
      <Field
        key={name}
        className={classes.container}
        component={TextField}
        fullWidth
        name={name}
        value={value}
        placeholder={placeholder || label}
        type="text"
        label={label}
        disabled={isDisabled || isLoading || readOnly}
        error={isTouched && !!error}
        newDesign
        icon={icon}
        InputProps={{ inputProps: { maxLength } }}
      />
    ),
    [value, isDisabled, isLoading, isTouched, error, icon],
  );
};
